/**
 * useBrainDumpCapture — orchestrates capture flow.
 *
 * 3 capture methods: captureText, captureVoice, capturePhoto.
 * Each handles: create note -> upload (if media) -> confirm -> trigger processing.
 *
 * apiClient returns { ok, data, ... } — unwrap via result.data
 */

import { useState, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';

async function _captureLocation() {
  if (!navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 },
    );
  });
}

async function _createNote({ rawText, captureType, location, clientId }) {
  const res = await apiClient.post('/api/v1/aif/brain-dump/notes', {
    raw_text: rawText,
    capture_type: captureType,
    location: location || undefined,
    client_id: clientId,
  });
  return res.data ?? res;
}

async function _uploadAttachment(noteId, file, attachmentType) {
  // Step 1: init presigned URL
  const initRes = await apiClient.post(
    `/api/v1/aif/brain-dump/notes/${noteId}/attachments/init`,
    { attachment_type: attachmentType, mime_type: file.type },
  );
  const initData = initRes.data ?? initRes;
  const { upload_url, fields, attachment_id, storage_path } = initData;

  // Step 2: direct browser -> S3
  const formData = new FormData();
  if (fields) {
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  }
  formData.append('file', file);
  // eslint-disable-next-line no-restricted-syntax -- S3 presigned POST is external, not our API
  const s3Response = await fetch(upload_url, { method: 'POST', body: formData });
  if (!s3Response.ok) {
    throw new Error(`S3 upload failed: ${s3Response.status}`);
  }

  // Step 3: confirm
  const confirmRes = await apiClient.post(
    `/api/v1/aif/brain-dump/notes/${noteId}/attachments/confirm`,
    {
      attachment_id,
      attachment_type: attachmentType,
      mime_type: file.type,
      storage_path,
      file_size_bytes: file.size,
      duration_seconds: file.duration || undefined,
    },
  );
  return confirmRes.data ?? confirmRes;
}

export function useBrainDumpCapture() {
  const [isCapturing, setIsCapturing] = useState(false);
  const [lastError, setLastError] = useState(null);

  const captureText = useCallback(async (text) => {
    setIsCapturing(true);
    setLastError(null);
    try {
      const location = await _captureLocation();
      const clientId = `text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      return await _createNote({ rawText: text, captureType: 'text', location, clientId });
    } catch (err) {
      setLastError(err.message || 'Capture failed');
      throw err;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const captureVoice = useCallback(async (blob, durationSeconds = null) => {
    setIsCapturing(true);
    setLastError(null);
    try {
      const location = await _captureLocation();
      const clientId = `voice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const note = await _createNote({ rawText: null, captureType: 'voice', location, clientId });
      const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
      if (durationSeconds) file.duration = durationSeconds;
      await _uploadAttachment(note.id, file, 'voice');
      return note;
    } catch (err) {
      setLastError(err.message || 'Voice capture failed');
      throw err;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const capturePhoto = useCallback(async (file) => {
    setIsCapturing(true);
    setLastError(null);
    try {
      const location = await _captureLocation();
      const clientId = `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const note = await _createNote({ rawText: null, captureType: 'photo', location, clientId });
      await _uploadAttachment(note.id, file, 'photo');
      return note;
    } catch (err) {
      setLastError(err.message || 'Photo capture failed');
      throw err;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  return { captureText, captureVoice, capturePhoto, isCapturing, lastError };
}

export function useRecentCaptures(limit = 5) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/aif/brain-dump/notes?limit=${limit}`);
      const data = res.data ?? res;
      setNotes(data.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  return { notes, loading, refetch };
}
